// Mobile Home dashboard. In-season it centers on a Box score ⇄ Rotation toggle:
//  • Box score — the starting five's SEASON per-game box line (MIN/PTS/REB/AST/FG/3P/FT/STL/BLK/
//    TO/±), horizontally scrollable with a pinned player column, "Show all" to expand the bench.
//  • Rotation — the in-season lineup editor (★ starters + Low/Med/High minutes), moved here from
//    the Roster screen. Writes reorderRosterDrag / updatePlayingTime.
// Below the box view: team stats w/ league ranks, team + league leaders, cash flow, headlines.
// Over ZenGM's leagueDashboard view.

import { useEffect, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { helpers } from "../util/helpers.ts";
import { toWorker } from "../util/toWorker.ts";
import { PHASE } from "../../common/constants.ts";
import type { View } from "../../common/types.ts";

// League Headlines come from stored game-event HTML that wraps player names, stat lines, and
// scores in <a> links (player / box-score / game-log routes). We want plain headlines on the
// mobile home, so drop the anchor tags while keeping their inner text and any other markup
// (e.g. the colored W/L span).
const stripLinks = (html: unknown) =>
	typeof html === "string" ? html.replace(/<\/?a\b[^>]*>/gi, "") : "";

const ordinal = (n: number) => {
	const s = ["th", "st", "nd", "rd"];
	const v = n % 100;
	return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF", SG: "#2a7de1", SF: "#18E0A8", PF: "#E8B923", C: "#E2504E",
	G: "#4A9EFF", GF: "#2a7de1", F: "#18E0A8", FC: "#E8B923",
};

const PHASE_LABEL: Record<number, string> = {
	[PHASE.PRESEASON]: "PRESEASON",
	[PHASE.REGULAR_SEASON]: "REGULAR SEASON",
	[PHASE.AFTER_TRADE_DEADLINE]: "REGULAR SEASON",
	[PHASE.PLAYOFFS]: "PLAYOFFS",
};

// Low / Med / High -> ZenGM playing-time modifier presets (mirrors GmRoster).
const PT = [
	{ label: "Low", val: 0.75, color: "#E8B923" },
	{ label: "Med", val: 1, color: "#4A9EFF" },
	{ label: "High", val: 1.5, color: "#18E0A8" },
];
const ptLevel = (v: number) => (v < 1 ? 0 : v > 1 ? 2 : 1);

// Whole-number per-game values for the box score.
const rs = (v: number | undefined) => `${Math.round(v ?? 0)}`;
const madeAtt = (m: number | undefined, a: number | undefined) =>
	`${Math.round(m ?? 0)}-${Math.round(a ?? 0)}`;

// A stat column: label, and how to render a player's per-game value.
const BOX_COLS: {
	key: string;
	render: (p: any) => string;
	cls?: (p: any) => string;
}[] = [
	{ key: "MIN", render: (p) => rs(p.stats.min) },
	{ key: "PTS", render: (p) => rs(p.stats.pts), cls: () => "pts" },
	{ key: "REB", render: (p) => rs(p.stats.trb) },
	{ key: "AST", render: (p) => rs(p.stats.ast) },
	{ key: "FG", render: (p) => madeAtt(p.stats.fg, p.stats.fga), cls: () => "split" },
	{ key: "3P", render: (p) => madeAtt(p.stats.tp, p.stats.tpa), cls: () => "split" },
	{ key: "FT", render: (p) => madeAtt(p.stats.ft, p.stats.fta), cls: () => "split" },
	{ key: "STL", render: (p) => rs(p.stats.stl) },
	{ key: "BLK", render: (p) => rs(p.stats.blk) },
	{ key: "TO", render: (p) => rs(p.stats.tov) },
	{
		key: "+/-",
		render: (p) => {
			const v = Math.round(p.stats.pm ?? 0);
			return `${v > 0 ? "+" : ""}${v}`;
		},
		cls: (p) => {
			const v = p.stats.pm ?? 0;
			return v > 0 ? "pm-pos" : v < 0 ? "pm-neg" : "pm-zero";
		},
	},
];

const rankColor = (rank: number, total: number) => {
	const frac = rank / Math.max(1, total);
	if (frac <= 0.34) return "#18E0A8";
	if (frac <= 0.67) return "#E8B923";
	return "#E2504E";
};

const GmHome = (props: View<"leagueDashboard">) => {
	const {
		won,
		lost,
		rank,
		region,
		name,
		confName,
		numGames,
		gp,
		teamStats,
		teamLeaders,
		leagueLeaders,
		revenue,
		profit,
		cash,
		payroll,
		showPlayoffSeries,
		seriesTitle,
		series,
		events,
		roster,
		starters,
		numPlayersOnCourt,
		maxPlayoffSeed,
	} = props as any;

	useTitleBar({ title: "Dashboard" });
	const { teamInfoCache, userTid, phase, season } = useLocal([
		"teamInfoCache",
		"userTid",
		"phase",
		"season",
	]);
	const team = teamInfoCache[userTid];

	const [tab, setTab] = useState<"box" | "rotation" | "standings">("box");
	const [showAll, setShowAll] = useState(false);

	// Standings (division card + full standings tab), fetched once from the worker.
	const [standings, setStandings] = useState<any | null>(null);
	const [stdView, setStdView] = useState<"conf" | "league" | "div">("conf");
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const res = await toWorker("main", "getStandings", undefined);
			if (!cancelled) setStandings(res);
		})();
		return () => {
			cancelled = true;
		};
	}, [season, gp, won, lost]);

	const N = numPlayersOnCourt ?? 5;
	// `starters` is the starting five (sent by every build); `roster` is the full team (new
	// build only). Fall back to starters so the box still renders against an older worker.
	const starters5: any[] = Array.isArray(starters) ? starters : [];
	const players: any[] =
		Array.isArray(roster) && roster.length > 0 ? roster : starters5;

	// Rotation editor state (mirrors GmRoster; writes to the same worker actions).
	const byPid = new Map(players.map((p) => [p.pid, p]));
	const [order, setOrder] = useState<number[]>(() => players.map((p) => p.pid));
	const [ptMap, setPtMap] = useState<Record<number, number>>(() =>
		Object.fromEntries(players.map((p) => [p.pid, Number(p.ptModifier)])),
	);
	const toggleStar = (pid: number) => {
		const idx = order.indexOf(pid);
		const wasStarter = idx < N;
		const arr = order.filter((x) => x !== pid);
		arr.splice(wasStarter ? N : N - 1, 0, pid);
		setOrder(arr);
		toWorker("main", "reorderRosterDrag", arr);
	};
	const setPt = (pid: number, val: number) => {
		setPtMap((m) => ({ ...m, [pid]: val }));
		toWorker("main", "updatePlayingTime", { pid, ptModifier: val });
	};

	const boxPlayers = showAll ? players : players.slice(0, N);
	const hasBox = boxPlayers.length > 0;
	const totalGP = numGames ?? 82;
	const played = gp ?? won + lost;
	const overline = PHASE_LABEL[phase] ?? "SEASON";
	// ZenGM's `season` is the ENDING year (season 2027 = the 2026-27 season), so the range is
	// (season-1)–season, not season–(season+1). The latter rendered the 2026-27 season -- the
	// one the league actually starts in -- as "2027-28", a year ahead of the rosters.
	const seasonLabel =
		season !== undefined
			? `${season - 1}–${String(season % 100).padStart(2, "0")}`
			: "";
	const conf = (confName ?? "").replace(/ Conference$/, "");
	const inPlayoffs =
		typeof rank === "number" &&
		typeof maxPlayoffSeed === "number" &&
		rank <= maxPlayoffSeed;

	const LeaderList = ({ list }: { list: any[] }) => (
		<div className="gmh-leaders">
			{(list ?? []).slice(0, 3).map((l) => (
				<div className="gmh-leader" key={l.stat}>
					<span className="gmh-leader-name">
						{l.firstNameShort ?? l.firstName} {l.lastName}
						{l.abbrev ? <span className="gmh-leader-tm"> {l.abbrev}</span> : null}
					</span>
					<span className="gmh-leader-val">
						{helpers.roundStat(l.value, l.stat)}{" "}
						<span className="gmh-leader-stat">{l.stat}</span>
					</span>
				</div>
			))}
		</div>
	);

	// --- standings helpers ---
	const clinchBadge = (c: string | undefined) => {
		switch (c) {
			case "z":
				return { label: "z", color: "#18E0A8", title: "Clinched #1 seed · home court" };
			case "y":
				return { label: "y", color: "#18E0A8", title: "Clinched first-round bye" };
			case "x":
				return { label: "x", color: "#4fd6a8", title: "Clinched playoff berth" };
			case "w":
				return { label: "pi", color: "#E8B923", title: "Clinched play-in berth" };
			case "o":
				return { label: "e", color: "#E2504E", title: "Eliminated" };
			default:
				return null;
		}
	};
	const ppgOf = (t: any) => (t.gp > 0 ? t.pts / t.gp : 0);
	const oppOf = (t: any) => (t.gp > 0 ? t.oppPts / t.gp : 0);
	const gbText = (v: number) => (!v ? "—" : v % 1 === 0 ? `${v}` : v.toFixed(1));
	const streakText = (s: number) => (s > 0 ? `W${s}` : s < 0 ? `L${-s}` : "—");

	const StandingsTable = ({
		teams,
		rankKey,
		gbKey,
		lines,
	}: {
		teams: any[];
		rankKey: string;
		gbKey: string;
		lines?: boolean;
	}) => {
		const sorted = [...teams].sort((a, b) => a[rankKey] - b[rankKey]);
		const seedLine = standings?.maxPlayoffSeedNoPlayIn;
		const playInLine = standings?.maxPlayoffSeed;
		return (
			<div className="gmh-std">
				<div className="gmh-std-row gmh-std-head">
					<span className="gmh-std-team">TEAM</span>
					<span>W-L</span>
					<span>GB</span>
					<span>PPG</span>
					<span>OPP</span>
					<span>DIFF</span>
					<span>STRK</span>
				</div>
				{sorted.map((t, i) => {
					const badge = clinchBadge(t.clinched);
					const d = ppgOf(t) - oppOf(t);
					return (
						<div key={t.tid}>
							<div className={`gmh-std-row ${t.tid === userTid ? "me" : ""}`}>
								<span className="gmh-std-team">
									<span className="gmh-std-rank">{i + 1}</span>
									{badge ? (
										<span
											className="gmh-std-badge"
											style={{ color: badge.color, borderColor: badge.color }}
											title={badge.title}
										>
											{badge.label}
										</span>
									) : null}
									<span className="gmh-std-abbr">{t.abbrev}</span>
								</span>
								<span>
									{t.won}-{t.lost}
								</span>
								<span className="gmh-std-mut">{gbText(t[gbKey])}</span>
								<span>{ppgOf(t).toFixed(1)}</span>
								<span className="gmh-std-mut">{oppOf(t).toFixed(1)}</span>
								<span
									style={{
										color: d > 0 ? "#18E0A8" : d < 0 ? "#E2504E" : "#cfe0ec",
									}}
								>
									{d > 0 ? "+" : ""}
									{d.toFixed(1)}
								</span>
								<span>{streakText(t.streak)}</span>
							</div>
							{lines && seedLine && i + 1 === seedLine ? (
								<div className="gmh-std-cut" />
							) : null}
							{lines && playInLine && playInLine !== seedLine && i + 1 === playInLine ? (
								<div className="gmh-std-cut dotted" />
							) : null}
						</div>
					);
				})}
			</div>
		);
	};

	const userDivName =
		standings?.divs?.find((d: any) => d.did === standings.userDid)?.name ??
		"Division";

	return (
		<div className="gmh">
			<style>{styles}</style>

			<header className="gmh-header">
				<div className="gmh-crest">{team?.abbrev ?? "—"}</div>
				<div>
					<div className="gmh-team">
						{team ? `${team.region} ${team.name}` : `${region} ${name}`}
					</div>
					<div className="gmh-teamsub">
						{seasonLabel ? `${seasonLabel} Season` : "Dashboard"}
					</div>
				</div>
			</header>

			<section className="gmh-record">
				<div className="gmh-record-over">
					{overline}
					{typeof rank === "number" && conf
						? ` · ${ordinal(rank)} in the ${conf}`
						: ""}
				</div>
				<div className="gmh-record-row">
					<div className="gmh-record-big">
						{won}–{lost}
					</div>
					<div className="gmh-record-gp">
						{played}/{totalGP} GP
					</div>
				</div>
				<div className="gmh-record-sub">
					{typeof rank === "number"
						? `${inPlayoffs ? "In the playoff picture" : "Outside the playoffs"}`
						: "Season not started"}
				</div>
				<div className="gmh-progress">
					<div
						className="gmh-progress-fill"
						style={{ width: `${Math.min(100, (played / totalGP) * 100)}%` }}
					/>
				</div>
			</section>

			{showPlayoffSeries && series ? (
				<div className="gmh-playoff">
					<div className="gmh-playoff-title">{seriesTitle}</div>
					<div className="gmh-playoff-match">
						{(series as any).away
							? `${(series as any).away.region} ${(series as any).away.won}`
							: "—"}
						{"  ·  "}
						{(series as any).home
							? `${(series as any).home.region} ${(series as any).home.won}`
							: "—"}
					</div>
				</div>
			) : null}

			<div className="gmh-toggle">
				<button
					className={tab === "box" ? "active" : ""}
					onClick={() => setTab("box")}
				>
					Dashboard
				</button>
				<button
					className={tab === "standings" ? "active" : ""}
					onClick={() => setTab("standings")}
				>
					Standings
				</button>
				<button
					className={tab === "rotation" ? "active" : ""}
					onClick={() => setTab("rotation")}
				>
					Rotation
				</button>
			</div>

			{tab === "box" ? (
				<>
					<section className="gmh-boxcard">
						<div className="gmh-box-h">
							<span>STARTING FIVE · SEASON PER GAME</span>
							{played > 0 ? <span>{played} GP</span> : <span>no games yet</span>}
						</div>
						<div className="gmh-box-scroll">
							<table className="gmh-box">
								<thead>
									<tr>
										<th className="gmh-box-name">PLAYER</th>
										{BOX_COLS.map((c) => (
											<th key={c.key}>{c.key}</th>
										))}
									</tr>
								</thead>
								<tbody>
									{boxPlayers.map((p) => {
										const posColor = POS_COLORS[p.ratings.pos] ?? "#55606a";
										return (
											<tr key={p.pid}>
												<td className="gmh-box-name">
													<span
														className="gmh-box-dot"
														style={{ background: posColor }}
													>
														{p.ratings.pos}
													</span>
													<span className="gmh-box-pname">
														{p.firstNameShort ?? p.firstName} {p.lastName}
													</span>
												</td>
												{BOX_COLS.map((c) => (
													<td key={c.key} className={c.cls ? c.cls(p) : ""}>
														{c.render(p)}
													</td>
												))}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						{!hasBox ? (
							<div className="gmh-boxempty">
								Box score unavailable — reload to refresh.
							</div>
						) : null}
						{players.length > N ? (
							<button
								className="gmh-showall"
								onClick={() => setShowAll((s) => !s)}
							>
								{showAll
									? "Show starters ▴"
									: `Show all ${players.length} ▾`}
							</button>
						) : null}
					</section>

					{standings && standings.userDid !== undefined ? (
						<section className="gmh-card">
							<div className="gmh-card-h gmh-std-cardh">
								<span>STANDINGS · {userDivName}</span>
								<button
									className="gmh-std-more"
									onClick={() => setTab("standings")}
								>
									Full standings ›
								</button>
							</div>
							<StandingsTable
								teams={standings.teams.filter(
									(t: any) => t.did === standings.userDid,
								)}
								rankKey="rankDiv"
								gbKey="gbDiv"
							/>
						</section>
					) : null}

					{Array.isArray(teamStats) && teamStats.length > 0 ? (
						<section className="gmh-card">
							<div className="gmh-card-h">TEAM STATS · LEAGUE RANK</div>
							<div className="gmh-teamstats">
								{(teamStats as any[]).map((s) => (
									<div className="gmh-ts-row" key={s.stat}>
										<span className="gmh-ts-name">{s.name}</span>
										<span className="gmh-ts-val">
											{helpers.roundStat(s.value, s.stat)}
										</span>
										<span
											className="gmh-ts-rank"
											style={{ color: rankColor(s.rank, 30) }}
										>
											{ordinal(s.rank)}
										</span>
									</div>
								))}
							</div>
						</section>
					) : null}

					<div className="gmh-two">
						<section className="gmh-card">
							<div className="gmh-card-h">TEAM LEADERS</div>
							<LeaderList list={teamLeaders as any[]} />
						</section>
						<section className="gmh-card">
							<div className="gmh-card-h">LEAGUE LEADERS</div>
							<LeaderList list={leagueLeaders as any[]} />
						</section>
					</div>

					<section className="gmh-card gmh-fin">
						<div className="gmh-card-h">CASH FLOW</div>
						<div className="gmh-fin-grid">
							<div>
								<span>Cash</span>
								<b>{helpers.formatCurrency(cash / 1000, "M")}</b>
							</div>
							<div>
								<span>Revenue</span>
								<b>{helpers.formatCurrency(revenue, "M")}</b>
							</div>
							<div>
								<span>Profit</span>
								<b>{helpers.formatCurrency(profit, "M")}</b>
							</div>
							<div>
								<span>Payroll</span>
								<b>{helpers.formatCurrency(payroll / 1000, "M")}</b>
							</div>
						</div>
					</section>

					{Array.isArray(events) && events.length > 0 ? (
						<section className="gmh-card">
							<div className="gmh-card-h">LEAGUE HEADLINES</div>
							{(events as any[]).slice(0, 6).map((e, i) => (
								<div
									className="gmh-headline"
									key={i}
									dangerouslySetInnerHTML={{ __html: stripLinks(e.text) }}
								/>
							))}
						</section>
					) : null}
				</>
			) : tab === "standings" ? (
				<>
					<div className="gmh-std-toggle">
						{(["conf", "league", "div"] as const).map((v) => (
							<button
								key={v}
								className={stdView === v ? "active" : ""}
								onClick={() => setStdView(v)}
							>
								{v === "conf" ? "Conference" : v === "league" ? "League" : "Division"}
							</button>
						))}
					</div>
					{!standings ? (
						<div className="gmh-boxempty">Loading standings…</div>
					) : stdView === "league" ? (
						<section className="gmh-card">
							<StandingsTable
								teams={standings.teams}
								rankKey="rankLeague"
								gbKey="gbLeague"
							/>
						</section>
					) : stdView === "conf" ? (
						standings.confs.map((c: any) => (
							<section className="gmh-card" key={c.cid}>
								<div className="gmh-card-h">{c.name.toUpperCase()}</div>
								<StandingsTable
									teams={standings.teams.filter((t: any) => t.cid === c.cid)}
									rankKey="rankConf"
									gbKey="gbConf"
									lines={standings.playoffsByConf}
								/>
							</section>
						))
					) : (
						standings.divs.map((d: any) => (
							<section className="gmh-card" key={d.did}>
								<div className="gmh-card-h">{d.name.toUpperCase()}</div>
								<StandingsTable
									teams={standings.teams.filter((t: any) => t.did === d.did)}
									rankKey="rankDiv"
									gbKey="gbDiv"
								/>
							</section>
						))
					)}
					<section className="gmh-card gmh-std-legend">
						<div className="gmh-legend-grid">
							<span>
								<b style={{ color: "#18E0A8" }}>z</b> #1 · home court
							</span>
							<span>
								<b style={{ color: "#18E0A8" }}>y</b> bye
							</span>
							<span>
								<b style={{ color: "#4fd6a8" }}>x</b> playoffs
							</span>
							<span>
								<b style={{ color: "#E8B923" }}>pi</b> play-in
							</span>
							<span>
								<b style={{ color: "#E2504E" }}>e</b> eliminated
							</span>
						</div>
					</section>
				</>
			) : (
				<section className="gmh-rot">
					<p className="gmh-rot-intro">
						Tap ★ to set your starting five. Low · Med · High sets each player's
						minutes.
					</p>
					<div className="gmh-rot-head">
						<span>STARTING FIVE</span>
						<span>PLAYING TIME</span>
					</div>
					{order.map((pid, i) => {
						const p = byPid.get(pid);
						if (!p) return null;
						const starter = i < N;
						if (i === N) {
							return [
								<div className="gmh-rot-head gmh-rot-bench" key="bench-h">
									<span>BENCH</span>
									<span>PLAYING TIME</span>
								</div>,
								<RotRow
									key={pid}
									p={p}
									starter={starter}
									level={ptLevel(ptMap[pid] ?? 1)}
									onStar={() => toggleStar(pid)}
									onPt={(v) => setPt(pid, v)}
								/>,
							];
						}
						return (
							<RotRow
								key={pid}
								p={p}
								starter={starter}
								level={ptLevel(ptMap[pid] ?? 1)}
								onStar={() => toggleStar(pid)}
								onPt={(v) => setPt(pid, v)}
							/>
						);
					})}
					{players.length === 0 ? (
						<div className="gmh-empty">Roster is empty.</div>
					) : null}
				</section>
			)}
		</div>
	);
};

const RotRow = ({
	p,
	starter,
	level,
	onStar,
	onPt,
}: {
	p: any;
	starter: boolean;
	level: number;
	onStar: () => void;
	onPt: (v: number) => void;
}) => {
	const posColor = POS_COLORS[p.ratings.pos] ?? "#55606a";
	return (
		<div className="gmh-rot-row">
			<button className={`gmh-star ${starter ? "on" : ""}`} onClick={onStar}>
				★
			</button>
			<span className="gmh-rot-dot" style={{ background: posColor }} />
			<div className="gmh-rot-info">
				<div className="gmh-rot-name">
					{p.firstName} {p.lastName}
				</div>
				<div className="gmh-rot-meta">
					OVR {p.ratings.ovr} · Age {p.age} · {p.ratings.pos}
				</div>
			</div>
			<div className="gmh-pt">
				{PT.map((opt, i) => (
					<button
						key={opt.label}
						className={`gmh-seg ${level === i ? "active" : ""}`}
						style={
							level === i ? { background: opt.color, color: "#0a0a0a" } : undefined
						}
						onClick={() => onPt(opt.val)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
};

const styles = `
.gmh { max-width:760px; margin:0 auto; padding:16px 16px 40px; color:#fff; font-family:Montserrat,system-ui,sans-serif;
  background:linear-gradient(#182a3a,#121212 220px); background-color:#121212; }
.gmh-header { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
.gmh-crest { width:40px; height:40px; border-radius:50%; background:#22405a; display:flex; align-items:center;
  justify-content:center; font-weight:800; font-size:12px; }
.gmh-team { font-weight:800; font-size:17px; }
.gmh-teamsub { font-size:12px; color:#8fb3c9; }

.gmh-record { margin:6px 0 16px; }
.gmh-record-over { font-size:10px; letter-spacing:1.2px; color:#8fb3c9; font-weight:800; }
.gmh-record-row { display:flex; align-items:flex-end; justify-content:space-between; margin-top:2px; }
.gmh-record-big { font-weight:900; font-size:46px; letter-spacing:-2.5px; line-height:1; }
.gmh-record-gp { font-size:11px; font-weight:800; color:#9fb0bc; }
.gmh-record-sub { font-size:12.5px; color:#b9c6d1; font-weight:700; margin-top:6px; }
.gmh-progress { height:5px; border-radius:4px; background:rgba(255,255,255,.14); margin-top:8px; overflow:hidden; }
.gmh-progress-fill { height:100%; background:#18E0A8; border-radius:4px; }

.gmh-playoff { background:#1d3a52; border-radius:12px; padding:14px; margin-bottom:16px; text-align:center; }
.gmh-playoff-title { font-size:10px; letter-spacing:.6px; color:#8fb3c9; font-weight:800; }
.gmh-playoff-match { font-weight:800; font-size:16px; margin-top:4px; }

.gmh-toggle { display:flex; gap:4px; background:rgba(0,0,0,.35); border-radius:12px; padding:4px; margin-bottom:14px; }
.gmh-toggle button { flex:1; border:none; background:none; color:#cfcfcf; font-family:inherit; font-weight:800;
  font-size:12.5px; padding:9px 0; border-radius:9px; cursor:pointer; }
.gmh-toggle button.active { background:#18E0A8; color:#000; }

.gmh-boxcard { background:#181818; border:1px solid #232323; border-radius:14px; padding:14px 15px; margin-bottom:12px; }
.gmh-box-h { display:flex; justify-content:space-between; align-items:baseline; }
.gmh-box-h span:first-child { font-size:10px; letter-spacing:.4px; color:#8fb3c9; font-weight:800; }
.gmh-box-h span:last-child { font-size:10px; color:#7f8b93; font-weight:800; }
.gmh-box-scroll { margin:12px -15px 0; padding:0 15px; overflow-x:auto; }
.gmh-box { border-collapse:collapse; min-width:574px; }
.gmh-box th { font-size:8.5px; font-weight:800; color:#7f8b93; text-align:right; width:42px; padding:0 0 7px;
  border-bottom:1px solid #2a2a2a; }
.gmh-box th.gmh-box-name { text-align:left; }
.gmh-box td { font-size:12px; font-weight:800; color:#e6e6e6; text-align:right; width:42px; padding:8px 0;
  border-bottom:1px solid #202020; white-space:nowrap; }
.gmh-box tr:last-child td { border-bottom:none; }
.gmh-box-name { position:sticky; left:0; z-index:2; width:112px; min-width:112px; background:#181818;
  text-align:left !important; }
.gmh-box-dot { display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px;
  border-radius:7px; font-size:8.5px; font-weight:800; color:#0a0a0a; margin-right:8px; vertical-align:middle; }
.gmh-box-pname { font-size:12px; font-weight:700; }
.gmh-box td.pts { color:#18E0A8; }
.gmh-box td.split { color:#cfcfcf; font-weight:700; }
.gmh-box td.pm-pos { color:#8fd9a6; }
.gmh-box td.pm-neg { color:#e69a99; }
.gmh-box td.pm-zero { color:#9aa4ac; }
.gmh-showall { display:block; margin:12px auto 0; background:none; border:none; color:#18E0A8; font-family:inherit;
  font-size:11.5px; font-weight:800; cursor:pointer; }
.gmh-boxempty { text-align:center; color:#7f8b93; font-size:12px; font-weight:600; padding:14px 0 2px; }

.gmh-card { background:#181818; border:1px solid #232323; border-radius:13px; padding:14px; margin-bottom:12px; }
.gmh-card-h { font-size:9px; letter-spacing:.6px; color:#7f8b93; font-weight:800; margin-bottom:10px; }
.gmh-std-cardh { display:flex; justify-content:space-between; align-items:center; }
.gmh-std-more { background:none; border:none; color:#18E0A8; font-weight:800; font-size:11px;
  cursor:pointer; font-family:inherit; letter-spacing:0; }
.gmh-std-toggle { display:flex; gap:6px; margin-bottom:12px; }
.gmh-std-toggle button { flex:1; border:none; background:#181818; color:#9fb0bc; font-family:inherit;
  font-weight:800; font-size:12px; padding:9px; border-radius:9px; cursor:pointer; }
.gmh-std-toggle button.active { background:#18E0A8; color:#0a0a0a; }
.gmh-std-row { display:flex; align-items:center; font-size:11px; font-weight:700; padding:7px 0;
  border-top:1px solid #202020; }
.gmh-std-row.gmh-std-head { border-top:none; padding:0 0 6px; }
.gmh-std-row.me { background:rgba(24,224,168,.07); }
.gmh-std-row > span { flex:1; text-align:center; }
.gmh-std-row > span.gmh-std-team { flex:2.3; text-align:left; display:flex; align-items:center; gap:6px; min-width:0; }
.gmh-std-head span { color:#7f8b93; font-size:8.5px; font-weight:800; letter-spacing:.3px; }
.gmh-std-rank { color:#5f6b73; font-size:10px; font-weight:800; width:15px; text-align:right; flex:none; }
.gmh-std-badge { border:1px solid; border-radius:5px; font-size:8px; font-weight:900; padding:1px 3px;
  line-height:1.1; flex:none; }
.gmh-std-abbr { font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.gmh-std-mut { color:#8fb3c9; }
.gmh-std-cut { height:2px; background:#2f6b52; margin:1px 0; border-radius:2px; }
.gmh-std-cut.dotted { background:none; border-top:2px dotted #6f7b83; height:0; border-radius:0; }
.gmh-std-legend .gmh-legend-grid { display:flex; flex-wrap:wrap; gap:8px 16px; font-size:11px;
  color:#9fb0bc; font-weight:600; }
.gmh-legend-grid b { font-weight:900; margin-right:3px; }
.gmh-teamstats { display:flex; flex-direction:column; }
.gmh-ts-row { display:flex; align-items:baseline; gap:10px; padding:9px 0; border-top:1px solid #202020; }
.gmh-ts-row:first-child { border-top:none; }
.gmh-ts-name { flex:1; font-size:13px; font-weight:700; color:#cdd9e2; }
.gmh-ts-val { font-size:15px; font-weight:800; }
.gmh-ts-rank { font-size:12px; font-weight:800; min-width:34px; text-align:right; }
.gmh-two { display:flex; gap:10px; }
.gmh-two .gmh-card { flex:1; }
.gmh-leaders { display:flex; flex-direction:column; gap:9px; }
.gmh-leader { display:flex; justify-content:space-between; align-items:baseline; gap:8px; }
.gmh-leader-name { font-weight:700; font-size:13px; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.gmh-leader-tm { color:#8fb3c9; font-size:11px; }
.gmh-leader-val { font-weight:800; font-size:14px; white-space:nowrap; }
.gmh-leader-stat { color:#7f8b93; font-size:10px; font-weight:700; }
.gmh-fin-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.gmh-fin-grid > div { display:flex; flex-direction:column; }
.gmh-fin-grid span { font-size:10px; color:#8fb3c9; font-weight:600; }
.gmh-fin-grid b { font-size:16px; font-weight:800; }
.gmh-headline { font-size:13px; color:#cfe0ec; padding:8px 0; border-top:1px solid #232323; line-height:1.4; }
.gmh-headline:first-of-type { border-top:none; }
.gmh-headline a { color:#4A9EFF; }

.gmh-rot-intro { color:#8a8a8a; font-size:12px; margin:0 0 14px; }
.gmh-rot-head { display:flex; justify-content:space-between; font-size:9px; letter-spacing:.6px; color:#7f8b93;
  font-weight:800; margin:8px 4px; }
.gmh-rot-bench { margin-top:16px; }
.gmh-rot-row { display:flex; align-items:center; gap:10px; background:#181818; border:1px solid #232323;
  border-radius:11px; padding:10px 12px; margin-bottom:8px; }
.gmh-star { background:none; border:none; font-size:20px; color:#55606a; cursor:pointer; line-height:1; }
.gmh-star.on { color:#18E0A8; }
.gmh-rot-dot { width:9px; height:9px; border-radius:3px; flex:0 0 auto; }
.gmh-rot-info { flex:1; min-width:0; }
.gmh-rot-name { font-weight:800; font-size:14px; }
.gmh-rot-meta { font-size:10.5px; color:#8fb3c9; }
.gmh-pt { display:flex; gap:4px; background:#121212; border-radius:9px; padding:3px; }
.gmh-seg { border:none; background:none; color:#8a8a8a; font-size:11px; font-weight:800; padding:6px 10px;
  border-radius:7px; cursor:pointer; font-family:inherit; }
.gmh-empty { color:#8a8a8a; text-align:center; padding:30px; }
`;

export default GmHome;
